import { useCallback, useEffect, useRef } from 'react';
import {
  persistOngoingLectureObjective,
  persistOngoingLectureDecisionTree,
  persistOngoingLectureQuestionContext,
  persistOngoingLectureCaseStudy,
} from 'redux/api/class';
import { LectureAnswersState, LectureQuestion, LectureData } from '../types';
import { getEntryKey } from '../utils/questionIdentity';
import { notification } from 'antd';

interface UsePersistLectureAnswersProps {
  courseTag: string;
  lessonId: number;
  lecture: LectureData | null;
}

interface PersistObjectivePayload {
  course: string;
  lesson_id: number;
  questions: Array<{ time_spent: string; question_id: number; answer_id: number }>;
}

interface PersistDecisionTreePayload {
  time_spent: string;
  course: string;
  lesson_id: number;
  questions: Array<{
    time_spent: string;
    question_step_id: number;
    question_id: number;
    answer_id: number;
  }>;
}

interface PersistQuestionContextPayload {
  time_spent: string;
  course: string;
  lesson_id: number;
  questions: Array<{
    time_spent: string;
    question_id: number;
    context_answers: string;
  }>;
}

export const usePersistLectureAnswers = ({
  courseTag,
  lessonId,
  lecture,
}: UsePersistLectureAnswersProps) => {
  const persistTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPersistedAnswersRef = useRef<string>('');

  useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }
    };
  }, []);

  const persistObjectiveQuestions = useCallback(
    async (answers: LectureAnswersState, currentQuestion?: LectureQuestion) => {
      try {
        if (!courseTag || !lessonId || !lecture) {
          return;
        }

        const objectiveToSend =
          currentQuestion && currentQuestion.question_type === 'objective_questions'
            ? { [currentQuestion.id_questao]: answers.objective[currentQuestion.id_questao] }
            : answers.objective;

        // Só persiste questões com resposta de fato selecionada (inclusive "Não sei", id 0) —
        // navegar sem responder nunca deve gravar um rascunho de resposta no backend.
        const questions = Object.entries(objectiveToSend)
          .filter(([, answerId]) => answerId !== undefined && answerId !== null)
          .map(([questionId, answerId]) => ({
            time_spent: answers.questionTime[Number(questionId)] || '00:00:00',
            question_id: Number(questionId),
            answer_id: answerId as number,
          }));

        if (questions.length === 0) {
          return;
        }

        const payload: PersistObjectivePayload = {
          course: courseTag,
          lesson_id: lessonId,
          questions,
        };

        await persistOngoingLectureObjective(payload);
      } catch (error: any) {
        console.error('Erro ao salvar progresso (objective):', error?.data ?? error?.message);
        notification.warning({ message: 'Erro ao salvar progresso', duration: 2 });
      }
    },
    [courseTag, lessonId, lecture]
  );

  const persistDecisionTreeQuestions = useCallback(
    async (answers: LectureAnswersState, currentQuestion?: LectureQuestion) => {
      try {
        if (!courseTag || !lessonId || !lecture) {
          return;
        }
        const decisionTreeToSend =
          currentQuestion && currentQuestion.question_type === 'decision_tree'
            ? { [currentQuestion.id_questao]: answers.decisionTree[currentQuestion.id_questao] }
            : answers.decisionTree;

        const questions = Object.entries(decisionTreeToSend)
          .filter(([_, userStepAnswers]) => userStepAnswers && userStepAnswers.length > 0)
          .flatMap(([questionId, userStepAnswers]) => {
            return userStepAnswers.map((stepAnswer) => {
              let stepId = 0;
              if (currentQuestion && currentQuestion.question_type === 'decision_tree') {
                if (stepAnswer.stepType === 'multiple' && currentQuestion.step_multiple) {
                  const step = currentQuestion.step_multiple[stepAnswer.stepIndex];
                  stepId = step?.id || 0;
                } else if (stepAnswer.stepType === 'single' && currentQuestion.step_single) {
                  const step = currentQuestion.step_single[stepAnswer.stepIndex];
                  stepId = step?.id || 0;
                }
              }

              return {
                time_spent: answers.questionTime[Number(questionId)] || '00:00:00',
                question_step_id: stepId,
                question_id: Number(questionId),
                answer_id: stepAnswer.selectedAnswerId,
              };
            });
          });

        if (questions.length === 0) {
          return;
        }

        const payload: PersistDecisionTreePayload = {
          time_spent: answers.contador,
          course: courseTag,
          lesson_id: lessonId,
          questions,
        };

        await persistOngoingLectureDecisionTree(payload);
      } catch (error: any) {
        console.error('Erro ao salvar progresso (decision_tree):', error?.data ?? error?.message);
        notification.warning({ message: 'Erro ao salvar progresso', duration: 2 });
      }
    },
    [courseTag, lessonId, lecture]
  );

  const persistCaseStudyQuestions = useCallback(
    async (answers: LectureAnswersState, currentQuestion?: LectureQuestion) => {
      try {
        if (!courseTag || !lessonId || !lecture) return;

        // Com a questão atual em mãos, enviamos apenas as etapas QUE ELA COBRE: o balde
        // answers.caseStudy[id] é compartilhado por todas as entradas do mesmo Estudo de Caso,
        // e enviar o balde inteiro reenviaria as etapas irmãs a cada salvamento
        const questions =
          currentQuestion && currentQuestion.question_type === 'case_study'
            ? (currentQuestion.step_multiple ?? [])
                .filter(
                  (step) =>
                    answers.caseStudy[currentQuestion.id_questao]?.[step.id] !== undefined,
                )
                .map((step) => ({
                  time_spent: answers.questionTime[currentQuestion.id_questao] || '00:00:00',
                  question_step_id: step.id,
                  question_id: currentQuestion.id_questao,
                  answer_id: answers.caseStudy[currentQuestion.id_questao][step.id],
                }))
            : Object.entries(answers.caseStudy)
                .filter(([_, stepAnswers]) => stepAnswers && Object.keys(stepAnswers).length > 0)
                .flatMap(([questionId, stepAnswers]) =>
                  Object.entries(stepAnswers).map(([stepId, answerId]) => ({
                    time_spent: answers.questionTime[Number(questionId)] || '00:00:00',
                    question_step_id: Number(stepId),
                    question_id: Number(questionId),
                    answer_id: answerId,
                  })),
                );

        if (questions.length === 0) return;

        await persistOngoingLectureCaseStudy({
          time_spent: answers.contador,
          course: courseTag,
          lesson_id: lessonId,
          questions,
        });
      } catch (error: any) {
        console.error('Erro ao salvar progresso (case_study):', error?.data ?? error?.message);
        notification.warning({ message: 'Erro ao salvar progresso', duration: 2 });
      }
    },
    [courseTag, lessonId, lecture],
  );

  const persistContextQuestions = useCallback(
    async (answers: LectureAnswersState, currentQuestion?: LectureQuestion) => {
      try {
        if (!courseTag || !lessonId || !lecture) {
          return;
        }

        const contextToSend =
          currentQuestion && currentQuestion.question_type === 'question_context'
            ? { [currentQuestion.id_questao]: answers.context[currentQuestion.id_questao] }
            : answers.context;

        const questions = Object.entries(contextToSend)
          .filter(([_, answer]) => answer && answer.trim().length > 0)
          .map(([questionId, answer]) => {
            const contextAnswersJson = JSON.stringify({
              answer: answer,
              timestamp: new Date().toISOString(),
            });

            return {
              time_spent: answers.questionTime[Number(questionId)] || '00:00:00',
              question_id: Number(questionId),
              context_answers: contextAnswersJson,
            };
          });

        if (questions.length === 0) {
          return;
        }

        const payload: PersistQuestionContextPayload = {
          time_spent: answers.contador,
          course: courseTag,
          lesson_id: lessonId,
          questions,
        };

        await persistOngoingLectureQuestionContext(payload);
      } catch (error: any) {
        console.error('Erro ao salvar progresso (context):', error?.data ?? error?.message);
        notification.warning({ message: 'Erro ao salvar progresso', duration: 2 });
      }
    },
    [courseTag, lessonId, lecture]
  );

  const persistAnswers = useCallback(
    async (answers: LectureAnswersState, currentQuestion?: LectureQuestion) => {
      if (!courseTag || !lessonId || !lecture) {
        return;
      }
      const answersString = JSON.stringify({
        answers,
        entryKey: currentQuestion ? getEntryKey(currentQuestion) : null,
      });
      if (answersString === lastPersistedAnswersRef.current) {
        return;
      }

      lastPersistedAnswersRef.current = answersString;

      if (currentQuestion) {
        if (currentQuestion.question_type === 'objective_questions') {
          await persistObjectiveQuestions(answers, currentQuestion);
        } else if (currentQuestion.question_type === 'decision_tree') {
          await persistDecisionTreeQuestions(answers, currentQuestion);
        } else if (currentQuestion.question_type === 'question_context') {
          await persistContextQuestions(answers, currentQuestion);
        } else if (currentQuestion.question_type === 'case_study') {
          await persistCaseStudyQuestions(answers, currentQuestion);
        }
      } else {
        if (Object.keys(answers.objective).length > 0) {
          await persistObjectiveQuestions(answers);
        }
        if (Object.keys(answers.decisionTree).length > 0) {
          await persistDecisionTreeQuestions(answers);
        }
        if (Object.keys(answers.context).length > 0) {
          await persistContextQuestions(answers);
        }
        if (Object.keys(answers.caseStudy).length > 0) {
          await persistCaseStudyQuestions(answers);
        }
      }
    },
    [
      persistObjectiveQuestions,
      persistDecisionTreeQuestions,
      persistContextQuestions,
      persistCaseStudyQuestions,
      courseTag,
      lessonId,
      lecture,
    ]
  );

  const debouncedPersist = useCallback(
    (answers: LectureAnswersState, currentQuestion?: LectureQuestion, delay: number = 1000) => {
      if (!lecture) return;
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }

      persistTimeoutRef.current = setTimeout(() => {
        persistAnswers(answers, currentQuestion);
      }, delay);
    },
    [persistAnswers, lecture]
  );

  const persistImmediately = useCallback(
    (answers: LectureAnswersState, currentQuestion?: LectureQuestion) => {
      if (!lecture) return;
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }

      persistAnswers(answers, currentQuestion);
    },
    [persistAnswers, lecture]
  );

  return {
    debouncedPersist,
    persistImmediately,
  };
};
